import { z } from "zod";

// Base type — used everywhere (charts, docs, messages, friends, header, etc.)
export const IdentifiedAuthor = z.object({
    type: z.literal("identified"),
    // Profile id — used by social surfaces to link the author chip to
    // /profile/$id. Always present (the row exists in User).
    id: z.number(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    // 1–2 char fallback used when avatarUrl is null and the client renders an
    // initials SVG. Always populated server-side via `computeInitials` so every
    // surface (web, navigator, emails) shows the same letters.
    initials: z.string().min(1).max(2),
    // Whether the user has social access turned on. Surfaces use this to
    // disable the profile link and italicize the name on inactive accounts.
    isSocialEnabled: z.boolean(),
});
export type IdentifiedAuthor = z.infer<typeof IdentifiedAuthor>;

// Anonymous author — seen by other users for anonymous posts/comments.
// `realName` is only populated for Compass superusers; omitted for everyone else.
export const AnonymousAuthor = z.object({
    type: z.literal("anonymous"),
    name: z.literal("Anonymous"),
    realName: z.string().optional(),
});
export type AnonymousAuthor = z.infer<typeof AnonymousAuthor>;

// Anonymous self — seen by the posting user for their own anonymous posts/comments
export const AnonymousSelfAuthor = z.object({
    type: z.literal("anonymous-self"),
    name: z.string(),
    avatarUrl: z.string().nullable(),
});
export type AnonymousSelfAuthor = z.infer<typeof AnonymousSelfAuthor>;

// Compass Team author — used when a discussion has no individual author (authorId is null)
export const CompassTeamAuthor = z.object({
    type: z.literal("compass-team"),
    name: z.literal("Compass Team"),
});
export type CompassTeamAuthor = z.infer<typeof CompassTeamAuthor>;

// Deleted author — used when the underlying User.deletedAt is set. Clients
// render the placeholder name without linking to the (gone) profile; see
// `buildIdentifiedAuthor` below for the branch used on the server side.
export const DeletedAuthor = z.object({
    type: z.literal("deleted"),
    name: z.literal("Deleted User"),
    avatarUrl: z.null(),
});
export type DeletedAuthor = z.infer<typeof DeletedAuthor>;

/**
 * Returns 1–2 uppercase letters for an avatar fallback.
 * - Both first + last present → first letter of each ("First Last" → "FL")
 * - Only one populated → its first letter
 * - Neither populated → "?" (caller should render the anonymous SVG instead;
 *   "?" is only used as a defensive fallback so the schema's min(1) holds)
 */
export function computeInitials(user: {
    firstName?: string | null;
    lastName?: string | null;
}): string {
    const first = (user.firstName ?? "").trim();
    const last = (user.lastName ?? "").trim();
    const f = first ? first.charAt(0).toUpperCase() : "";
    const l = last ? last.charAt(0).toUpperCase() : "";
    if (f && l) return f + l;
    if (f) return f;
    if (l) return l;
    return "?";
}

/**
 * Returns "First Last" or "First Last (Inactive)" if the user is
 * soft-deleted or has neither portal enabled.
 *
 * Used server-side when constructing any IdentifiedAuthor-shaped
 * payload so clients can render the name verbatim without each
 * screen having to reimplement the inactivity suffix.
 */
export function formatAuthorName(user: {
    firstName: string;
    lastName: string;
    isSocialEnabled?: boolean | null;
    isPrivateEnabled?: boolean | null;
    deletedAt?: Date | string | null;
}): string {
    if (user.deletedAt != null) return "Deleted User";
    const baseName = `${user.firstName} ${user.lastName}`;
    const isDisabled = user.isSocialEnabled === false && user.isPrivateEnabled === false;
    return isDisabled ? `${baseName} (Inactive)` : baseName;
}

/**
 * Build the `DeletedAuthor | IdentifiedAuthor` payload for a user. Use this
 * at every server-side site that previously hand-rolled `{ type: "identified", ... }`
 * so a soft-deleted user consistently renders as "Deleted User" with no
 * avatar or profile link, while live-but-disabled users still fall through
 * to `formatAuthorName`'s "(Inactive)" suffix.
 */
export function buildIdentifiedAuthor(
    user: {
        id: number;
        firstName: string;
        lastName: string;
        isSocialEnabled: boolean;
        isPrivateEnabled?: boolean | null;
        deletedAt?: Date | string | null;
    },
    avatarUrl: string | null,
): DeletedAuthor | IdentifiedAuthor {
    if (user.deletedAt != null) {
        return { type: "deleted", name: "Deleted User", avatarUrl: null };
    }
    return {
        type: "identified",
        id: user.id,
        name: formatAuthorName(user),
        avatarUrl,
        initials: computeInitials(user),
        isSocialEnabled: user.isSocialEnabled,
    };
}

// Narrow union for surfaces (charts, documents, chart authors, thread author
// entry, etc.) that only ever produce an identified-or-deleted author — i.e.,
// no anonymous / compass-team variants apply.
export const IdentifiedOrDeletedAuthor = z.discriminatedUnion("type", [
    IdentifiedAuthor,
    DeletedAuthor,
]);
export type IdentifiedOrDeletedAuthor = z.infer<typeof IdentifiedOrDeletedAuthor>;

// Extended union — only for threads/comments/discussions/social posts
export const ThreadAuthor = z.discriminatedUnion("type", [
    IdentifiedAuthor,
    AnonymousAuthor,
    AnonymousSelfAuthor,
    CompassTeamAuthor,
    DeletedAuthor,
]);
export type ThreadAuthor = z.infer<typeof ThreadAuthor>;

/**
 * Canonical anonymity-aware author formatter for social surfaces (posts,
 * comments, discussion replies). Centralizes the four-way decision so call
 * sites don't reinvent the same `isAnonymous ? {...} : identified` ternary.
 *
 * - `isAnonymous=false` → `IdentifiedAuthor | DeletedAuthor` via `buildIdentifiedAuthor`
 * - `isAnonymous=true`, requester authored it → `AnonymousSelfAuthor` (their
 *    own real name suffixed with "(Anonymous)" so they recognize their own
 *    drafts in feeds)
 * - `isAnonymous=true`, requester is a Compass superuser viewing someone
 *    else's anonymous content → `AnonymousAuthor` with `realName` populated
 * - otherwise `AnonymousAuthor` with no `realName`
 */
export function buildSocialAuthor(
    user: {
        id: number;
        firstName: string;
        lastName: string;
        isSocialEnabled: boolean;
        isPrivateEnabled?: boolean | null;
        deletedAt?: Date | string | null;
    },
    opts: {
        isAnonymous: boolean;
        requestingUserId: number;
        isRequesterSuperuser: boolean;
        avatarUrl: string | null;
    },
): ThreadAuthor {
    if (!opts.isAnonymous) {
        return buildIdentifiedAuthor(user, opts.avatarUrl);
    }
    if (user.id === opts.requestingUserId) {
        return {
            type: "anonymous-self",
            name: `${user.firstName} ${user.lastName} (Anonymous)`,
            avatarUrl: opts.avatarUrl,
        };
    }
    return {
        type: "anonymous",
        name: "Anonymous",
        ...(opts.isRequesterSuperuser
            ? { realName: `${user.firstName} ${user.lastName}` }
            : {}),
    };
}
